import {
  BasketballLeague,
  BasketballLeagueDbb,
  BasketballLeagueDbbTeam,
  BasketballLeagueSelectedDbbTeam,
  BasketballLeagueTeamStats,
  BasketballLeagueUpcomingLeagueData,
} from '@hefe/models';
import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import API from '../../../api/api';
import {
  DbbBasketballLeagueCreateRequest,
  DbbBasketballLeaguePreviewRequest,
  DbbBasketballLeagueUpdateRequest,
  DbbLeagueSelectionRequest,
} from '../../../models/requests/basketball-league';
import { isCacheValid } from '../../../utils/cache/caching';

const FEATURE_KEY = 'basketballLeagues';

/************************************************************************************
 *
 *                                  ACTIONS
 *
 ************************************************************************************
 */
export const fetchBasketballLeagues = createAsyncThunk(
  'basketballLeagues/getAll',
  async (thunkAPI) => {
    const response = await API.basketballLeagues().getAll();
    return response?.data.data;
  }
);

export const fetchBasketballLeagueUpcomingData = createAsyncThunk(
  'basketballLeagues/getUpcomingData',
  async (leagueId: number, thunkAPI) => {
    const response = await API.basketballLeagues().getUpcomingData(leagueId);
    return response?.data;
  }
);

export const fetchBasketballLeagueTeamStats = createAsyncThunk<any, any>(
  'basketballLeagues/getTeamStats',
  async (
    data: { leagueId: number; teamId: number; cacheDuration: number },
    thunkAPI
  ) => {
    if (!isCacheValid('basketballLeagues/getTeamStats', data, thunkAPI)) {
      const response = await API.basketballLeagues().getScoutingReport(
        data.leagueId,
        data.teamId
      );

      if (response) {
        return thunkAPI.fulfillWithValue({
          teamId: data.teamId,
          leagueId: data.leagueId,
          stats: response?.data,
        });
      } else {
        return thunkAPI.rejectWithValue(null);
      }
    }

    return undefined;
  }
);

export const fetchDbbBasketballLeagues = createAsyncThunk(
  'basketballLeagues/getDbbBasketballLeagues',
  async (data: DbbLeagueSelectionRequest, thunkAPI) => {
    const response = await API.basketballLeagues().scannDBBLeagues(data);
    return response?.data.data;
  }
);

export const fetchDbbBasketballLeague = createAsyncThunk(
  'basketballLeagues/getDbbBasketballLeague',
  async (leagueId: number, thunkAPI) => {
    const response = await API.basketballLeagues().getLeagueData(leagueId);
    return response?.data;
  }
);

export const fetchDbbBasketballLeagueTeams = createAsyncThunk(
  'basketballLeagues/getDbbBasketballLeagueTeams',
  async (leagueId: number, thunkAPI) => {
    const response = await API.basketballLeagues().getTeamnames(leagueId);
    return response?.data;
  }
);

export const fetchDbbBasketballLeaguePreview = createAsyncThunk(
  'basketballLeagues/getDbbBasketballLeagueTeams',
  async (data: DbbBasketballLeaguePreviewRequest, thunkAPI) => {
    const response = await API.basketballLeagues().getPreview(data);
    return response?.data;
  }
);

export const createDbbBasketballLeague = createAsyncThunk(
  'basketballLeagues/createDbb',
  async (data: DbbBasketballLeagueCreateRequest, thunkAPI) => {
    const response = await API.basketballLeagues().create(data);
    return response?.data;
  }
);

export const updateDbbBasketballLeague = createAsyncThunk(
  'basketballLeagues/update',
  async (data: DbbBasketballLeagueUpdateRequest, thunkAPI) => {
    const response = await API.basketballLeagues().update(data);
    return response?.data;
  }
);

export const deleteBasketballLeague = createAsyncThunk<any, BasketballLeague>(
  'basketballLeagues/delete',
  async (request: BasketballLeague, thunkAPI) => {
    const response = await API.basketballLeagues().delete(request);
    if (response) {
      return thunkAPI.fulfillWithValue(request);
    } else {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

export const syncBasketballLeague = createAsyncThunk(
  'basketballLeagues/sync',
  async (leagueId: number, thunkAPI) => {
    const response = await API.basketballLeagues().syncLeague(leagueId);
    return response?.data.data;
  }
);

/************************************************************************************
 *
 *                                      SLICE
 *
 ************************************************************************************
 */

interface DbbLeagueSelectionResponse {
  verbaende: DbbLeagueSelectionResponseData[];
  gebiete: DbbLeagueSelectionResponseData[];
  ligatypen: DbbLeagueSelectionResponseData[];
  agkGeschlechtList: DbbLeagueSelectionResponseData[];
  altersklassen: DbbLeagueSelectionResponseData[];
  spielklassen: DbbLeagueSelectionResponseData[];
  ligaListe: {
    ligen: BasketballLeagueDbb[];
  };
  wam: DbbLeagueSelectionRequest;
}

interface DbbLeagueSelectionPair {
  value: any;
  text: string;
}

interface DbbLeagueSelectionSelected {
  verbandIds?: DbbLeagueSelectionPair;
  gebietIds?: DbbLeagueSelectionPair;
  ligatypIds?: DbbLeagueSelectionPair;
  akgGeschlechtIds?: DbbLeagueSelectionPair;
  alterklasseIds?: DbbLeagueSelectionPair;
  spielklassen?: DbbLeagueSelectionPair;
}

export interface DbbLeagueSelectionResponseData {
  id: string | number;
  label?: string;
  bezirk?: string;
  akg?: string;
  geschlecht?: string;
  hits: number;
}

export interface DbbLeagueSelectionSettings {
  ownReferees: boolean;
}
export interface BasketballLeaguesState {
  leagues: BasketballLeague[];
  upcomingLeagueData: { [key: number]: BasketballLeagueUpcomingLeagueData };
  leagueTeamStats: {
    [key: number]: { [key: number]: BasketballLeagueTeamStats };
  };
  assistent: {
    currentStep: number;
    selectedCountry?: string;
    selectedLeague?: BasketballLeagueDbb;
    selectedTeams: BasketballLeagueSelectedDbbTeam[];
    settings: DbbLeagueSelectionSettings;
    dbbLeagueSelection: {
      currentStep: number;
      data: DbbLeagueSelectionResponse;
      isLoading: boolean;
      selectedData: DbbLeagueSelectionSelected;
    };
  };
}

const initialState: BasketballLeaguesState = {
  leagues: [],
  upcomingLeagueData: {},
  leagueTeamStats: {},
  assistent: {
    currentStep: 0,
    selectedCountry: undefined,
    selectedLeague: undefined,
    selectedTeams: [],
    settings: {
      ownReferees: false,
    },
    dbbLeagueSelection: {
      currentStep: 0,
      data: {
        wam: {
          token: '0',
          verbandIds: [],
          gebietIds: [],
          ligatypIds: [],
          akgGeschlechtIds: [],
          alterklasseIds: [],
          spielklassen: [],
          sortBy: 1,
        },
        verbaende: [],
        gebiete: [],
        ligatypen: [],
        agkGeschlechtList: [],
        altersklassen: [],
        spielklassen: [],
        ligaListe: {
          ligen: [],
        },
      },
      isLoading: false,
      selectedData: {
        verbandIds: undefined,
        gebietIds: undefined,
        ligatypIds: undefined,
        akgGeschlechtIds: undefined,
        alterklasseIds: undefined,
        spielklassen: undefined,
      },
    },
  },
};

export const basketballLeaguesSlice = createSlice({
  name: FEATURE_KEY,
  initialState: initialState,
  reducers: {
    incrementStep: (state: BasketballLeaguesState) => {
      state.assistent.currentStep += 1;
    },
    decrementStep: (state: BasketballLeaguesState) => {
      state.assistent.currentStep =
        state.assistent.currentStep +
        (state.assistent.currentStep > 0 ? -1 : 0);
    },
    setStep: (state: BasketballLeaguesState, action: PayloadAction<number>) => {
      state.assistent.currentStep = action.payload;
    },
    setSelectedCountry: (
      state: BasketballLeaguesState,
      action: PayloadAction<string>
    ) => {
      state.assistent.selectedCountry = action.payload;
    },
    setSelectedLeague: (
      state: BasketballLeaguesState,
      action: PayloadAction<BasketballLeagueDbb>
    ) => {
      state.assistent.selectedLeague = action.payload;
      state.assistent.currentStep = state.assistent.currentStep + 1;

      if (state.assistent.selectedLeague?.ligaId !== action.payload.ligaId) {
        state.assistent.selectedTeams = [];
      }
    },
    setDbbLeagueSelectionStep: (
      state: BasketballLeaguesState,
      action: PayloadAction<number>
    ) => {
      state.assistent.dbbLeagueSelection.currentStep = action.payload;
    },
    incrementDbbLeagueSelectionStep: (state: BasketballLeaguesState) => {
      const currentStep = state.assistent.dbbLeagueSelection.currentStep;

      state.assistent.dbbLeagueSelection.currentStep =
        currentStep === 6 ? 6 : currentStep + 1;
    },
    decrementDbbLeagueSelectionStep: (state: BasketballLeaguesState) => {
      const currentStep = state.assistent.dbbLeagueSelection.currentStep;

      state.assistent.dbbLeagueSelection.currentStep =
        currentStep === 0 ? 0 : currentStep - 1;
    },
    setDbbLeagueSelectionData: (
      state: BasketballLeaguesState,
      action: PayloadAction<{
        text: string;
        value: string[];
      }>
    ) => {
      const currentStep = state.assistent.dbbLeagueSelection.currentStep;

      switch (currentStep) {
        case 0:
          state.assistent.dbbLeagueSelection.selectedData.verbandIds = {
            value: action.payload.value,
            text: action.payload.text,
          };
          state.assistent.dbbLeagueSelection.selectedData.gebietIds = undefined;
          state.assistent.dbbLeagueSelection.selectedData.ligatypIds =
            undefined;
          state.assistent.dbbLeagueSelection.selectedData.akgGeschlechtIds =
            undefined;
          state.assistent.dbbLeagueSelection.selectedData.alterklasseIds =
            undefined;
          state.assistent.dbbLeagueSelection.selectedData.spielklassen =
            undefined;
          break;
        case 1:
          state.assistent.dbbLeagueSelection.selectedData.gebietIds = {
            value: action.payload.value,
            text: action.payload.text,
          };
          state.assistent.dbbLeagueSelection.selectedData.ligatypIds =
            undefined;
          state.assistent.dbbLeagueSelection.selectedData.akgGeschlechtIds =
            undefined;
          state.assistent.dbbLeagueSelection.selectedData.alterklasseIds =
            undefined;
          state.assistent.dbbLeagueSelection.selectedData.spielklassen =
            undefined;
          break;
        case 2:
          state.assistent.dbbLeagueSelection.selectedData.ligatypIds = {
            value: action.payload.value,
            text: action.payload.text,
          };
          state.assistent.dbbLeagueSelection.selectedData.akgGeschlechtIds =
            undefined;
          state.assistent.dbbLeagueSelection.selectedData.alterklasseIds =
            undefined;
          state.assistent.dbbLeagueSelection.selectedData.spielklassen =
            undefined;
          break;
        case 3:
          state.assistent.dbbLeagueSelection.selectedData.akgGeschlechtIds = {
            value: action.payload.value,
            text: action.payload.text,
          };
          state.assistent.dbbLeagueSelection.selectedData.alterklasseIds =
            undefined;
          state.assistent.dbbLeagueSelection.selectedData.spielklassen =
            undefined;
          break;
        case 4:
          state.assistent.dbbLeagueSelection.selectedData.alterklasseIds = {
            value: action.payload.value,
            text: action.payload.text,
          };
          state.assistent.dbbLeagueSelection.selectedData.spielklassen =
            undefined;
          break;
        case 5:
          state.assistent.dbbLeagueSelection.selectedData.spielklassen = {
            value: action.payload.value,
            text: action.payload.text,
          };
          break;
        default:
          break;
      }

      // Increment step
      state.assistent.dbbLeagueSelection.currentStep =
        currentStep === 6 ? 6 : currentStep + 1;
    },
    setSelectedTeamClubTeamId: (
      state: BasketballLeaguesState,
      action: PayloadAction<{ index: number; teamId: number }>
    ) => {
      const team = state.assistent.selectedTeams[action.payload.index];
      if (team) {
        team.team_id = action.payload.teamId;
      } else {
        state.assistent.selectedTeams.push({
          value: '',
          label: '',
          season_team_id: undefined,
          team_id: action.payload.teamId,
        });
      }
    },
    setSelectedTeamSeasonTeamId: (
      state: BasketballLeaguesState,
      action: PayloadAction<{
        index: number;
        seasonTeamId: number;
        name: string;
      }>
    ) => {
      const team = state.assistent.selectedTeams[action.payload.index];
      if (team) {
        team.season_team_id = action.payload.seasonTeamId;
        team.label = action.payload.name;
        team.value = action.payload.name;
      } else {
        state.assistent.selectedTeams.push({
          value: action.payload.name,
          label: action.payload.name,
          season_team_id: action.payload.seasonTeamId,
          team_id: undefined,
        });
      }
    },
    setSelectedTeam: (
      state: BasketballLeaguesState,
      action: PayloadAction<BasketballLeagueSelectedDbbTeam & { index: number }>
    ) => {
      if (state.assistent.selectedTeams.length > action.payload.index) {
        state.assistent.selectedTeams[action.payload.index] = action.payload;
      } else {
        state.assistent.selectedTeams.push(action.payload);
      }
    },
    removeSelectedTeam: (
      state: BasketballLeaguesState,
      action: PayloadAction<BasketballLeagueSelectedDbbTeam>
    ) => {
      state.assistent.selectedTeams = state.assistent.selectedTeams.filter(
        (team) => team.season_team_id !== action.payload.season_team_id
      );
    },
    setLeagueSettings: (
      state: BasketballLeaguesState,
      action: PayloadAction<DbbLeagueSelectionSettings>
    ) => {
      state.assistent.settings = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchBasketballLeagues.fulfilled,
      (
        state: BasketballLeaguesState,
        action: PayloadAction<BasketballLeague[]>
      ) => {
        state.leagues = action.payload;
      }
    );
    builder.addCase(
      fetchDbbBasketballLeagues.pending,
      (state: BasketballLeaguesState) => {
        state.assistent.dbbLeagueSelection.isLoading = true;
      }
    );
    builder.addCase(
      fetchDbbBasketballLeagues.rejected,
      (state: BasketballLeaguesState) => {
        state.assistent.dbbLeagueSelection.isLoading = false;
      }
    );
    builder.addCase(
      fetchDbbBasketballLeagues.fulfilled,
      (
        state: BasketballLeaguesState,
        action: PayloadAction<DbbLeagueSelectionResponse>
      ) => {
        state.assistent.dbbLeagueSelection.isLoading = false;
        state.assistent.dbbLeagueSelection.data = action.payload;
      }
    );
    builder.addCase(
      fetchDbbBasketballLeagueTeams.fulfilled,
      (
        state: BasketballLeaguesState,
        action: PayloadAction<BasketballLeagueDbbTeam[]>
      ) => {
        if (state.assistent.selectedLeague) {
          state.assistent.selectedLeague.teams = action.payload;
        }
      }
    );
    builder.addCase(
      createDbbBasketballLeague.fulfilled,
      (
        state: BasketballLeaguesState,
        action: PayloadAction<{ data: BasketballLeague }>
      ) => {
        state.leagues.push(action.payload.data);
        // Reset assistant
        state.assistent = initialState.assistent;
      }
    );
    builder.addCase(
      deleteBasketballLeague.fulfilled,
      (
        state: BasketballLeaguesState,
        action: PayloadAction<BasketballLeague>
      ) => {
        state.leagues = state.leagues.filter(
          (league) => league.id !== action.payload.id
        );
      }
    );
    builder.addCase(
      updateDbbBasketballLeague.fulfilled,
      (
        state: BasketballLeaguesState,
        action: PayloadAction<BasketballLeague>
      ) => {
        state.leagues = state.leagues.map((league) =>
          league.id === action.payload.id ? action.payload : league
        );
      }
    );
    builder.addCase(
      fetchBasketballLeagueUpcomingData.fulfilled,
      (
        state: BasketballLeaguesState,
        action: PayloadAction<BasketballLeagueUpcomingLeagueData>
      ) => {
        if (action.payload) {
          state.upcomingLeagueData[action.payload.leagueId] = action.payload;
        }
      }
    );
    builder.addCase(
      fetchBasketballLeagueTeamStats.fulfilled,
      (
        state: BasketballLeaguesState,
        action: PayloadAction<{
          leagueId: number;
          teamId: number;
          stats: BasketballLeagueTeamStats;
        }>
      ) => {
        if (action.payload) {
          const leagueId = parseInt(
            action.payload.leagueId as unknown as string
          );
          if (!state.leagueTeamStats[leagueId]) {
            state.leagueTeamStats[leagueId] = {};
          }

          state.leagueTeamStats[leagueId][action.payload.teamId] =
            action.payload.stats;
        }
      }
    );
  },
});

/**
 * Export some helper functions 🥳
 */

/**
 * This function inserts a value into the correct propertry (depending on current step) of BasketballLeagueRequest
 * and returns it
 * @param state
 * @param step
 * @param value
 */
export function getDbbBasketballLeagueRequestData(
  state: BasketballLeaguesState,
  value?: string
): DbbLeagueSelectionRequest {
  const request = state.assistent.dbbLeagueSelection.data.wam;

  // Return initial state if no value passed
  if (!value) {
    return request;
  }

  switch (state.assistent.dbbLeagueSelection.currentStep) {
    case 0:
      return {
        ...initialState.assistent.dbbLeagueSelection.data.wam,
        verbandIds: [parseInt(value)],
      };
    case 1:
      return {
        ...request,
        gebietIds: [value],
        ligatypIds: [],
        akgGeschlechtIds: [],
        alterklasseIds: [],
        spielklassen: [],
      };
    case 2:
      return {
        ...request,
        ligatypIds: [parseInt(value)],
        akgGeschlechtIds: [],
        alterklasseIds: [],
        spielklassen: [],
      };
    case 3:
      return {
        ...request,
        akgGeschlechtIds: [value],
        alterklasseIds: [],
        spielklassen: [],
      };
    case 4:
      return {
        ...request,
        alterklasseIds: [parseInt(value)],
        spielklassen: [],
      };
    case 5:
      return {
        ...request,
        spielklassen: [parseInt(value)],
      };
    default:
      return initialState.assistent.dbbLeagueSelection.data.wam;
  }
}

/**
 * Get current step selected Data
 */
export const getDbbBasketballLeagueSelectedValueForStep = (
  state: BasketballLeaguesState,
  step: number
): string | undefined => {
  switch (step) {
    case 0:
      return state.assistent.dbbLeagueSelection.selectedData.verbandIds?.text;
    case 1:
      return state.assistent.dbbLeagueSelection.selectedData.gebietIds?.text;
    case 2:
      return state.assistent.dbbLeagueSelection.selectedData.ligatypIds?.text;
    case 3:
      return state.assistent.dbbLeagueSelection.selectedData.akgGeschlechtIds
        ?.text;
    case 4:
      return state.assistent.dbbLeagueSelection.selectedData.alterklasseIds
        ?.text;
    case 5:
      return state.assistent.dbbLeagueSelection.selectedData.spielklassen?.text;
    default:
      return '';
  }
};

/*
 * Export reducer for store configuration.
 */
export const basketballLeaguesReducer = basketballLeaguesSlice.reducer;
export const basketballLeaguesActions = basketballLeaguesSlice.actions;

const state = (rootState: any): BasketballLeaguesState =>
  rootState[FEATURE_KEY];

export const basketballLeaguesStateSelector = createSelector(state, (state) => {
  return state;
});

export const basketballLeaguesSelector = createSelector(state, (state) => {
  return state.leagues;
});

export const basketballLeagueAssistentSelector = createSelector(
  state,
  (state) => {
    return state.assistent;
  }
);

export const basketballLeagueDbbSelectionCurrentStepDataSelector =
  createSelector(state, (state) => {
    switch (state.assistent.dbbLeagueSelection.currentStep) {
      case 0:
        return state.assistent.dbbLeagueSelection.data.verbaende;
      case 1:
        return state.assistent.dbbLeagueSelection.data.gebiete;
      case 2:
        return state.assistent.dbbLeagueSelection.data.ligatypen;
      case 3:
        return state.assistent.dbbLeagueSelection.data.agkGeschlechtList;
      case 4:
        return state.assistent.dbbLeagueSelection.data.altersklassen;
      case 5:
        return state.assistent.dbbLeagueSelection.data.spielklassen;
      case 6:
        return state.assistent.dbbLeagueSelection.data.ligaListe.ligen;
    }

    return [];
  });

export const basketballLeagueUpcomingLeagueDataSelector =
  (leagueId: number) => (state: any) => {
    const teamsState = state[FEATURE_KEY] as BasketballLeaguesState;

    return teamsState.upcomingLeagueData[leagueId];
  };

export const basketballLeagueTeamStatsSelector =
  (data: { leagueId: number; teamId: number }) => (state: any) => {
    const teamsState = state[FEATURE_KEY] as BasketballLeaguesState;

    if (teamsState.leagueTeamStats[data.leagueId]) {
      return teamsState.leagueTeamStats[data.leagueId][data.teamId];
    }

    return undefined;
  };
